
export function levenshteinDistance(a, b) {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = [];

  // Initialize first row and column
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  // Fill the matrix
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

/**
 * Find the best matching station from suggestions using fuzzy matching
 * @param {string} query - User input query
 * @param {Array<string>} suggestions - Array of station suggestions
 * @param {number} maxDistance - Maximum allowed distance (default: 3)
 * @returns {string|null} - Best matching station or null if no good match
 */
export function findBestMatch(query, suggestions, maxDistance = 3) {
  if (!query || !suggestions || suggestions.length === 0) {
    return null;
  }

  const normalizedQuery = query.toLowerCase().trim();

  // First, try exact match
  const exactMatch = suggestions.find(station =>
    station.toLowerCase() === normalizedQuery
  );
  if (exactMatch) return exactMatch;

  // Then try starts with match
  const startsWithMatch = suggestions.find(station =>
    station.toLowerCase().startsWith(normalizedQuery)
  );
  if (startsWithMatch) return startsWithMatch;

  // Then try contains match
  const containsMatch = suggestions.find(station =>
    station.toLowerCase().includes(normalizedQuery)
  );
  if (containsMatch) return containsMatch;

  // Finally, use Levenshtein distance for fuzzy matching
  let bestMatch = null;
  let minDistance = Infinity;

  for (const station of suggestions) {
    const distance = levenshteinDistance(normalizedQuery, station.toLowerCase());
    if (distance < minDistance && distance <= maxDistance) {
      minDistance = distance;
      bestMatch = station;
    }
  }

  return bestMatch;
}

/**
 * Get filtered suggestions based on user input
 * @param {string} query - User input query
 * @param {Array<string>} suggestions - Array of station suggestions
 * @param {number} limit - Maximum number of suggestions to return (default: 5)
 * @returns {Array<string>} - Filtered and sorted suggestions
 */
export function getFilteredSuggestions(query, suggestions, limit = 5) {
  if (!query || query.length < 1 || !suggestions || suggestions.length === 0) {
    return [];
  }

  const normalizedQuery = query.toLowerCase().trim();
  const results = [];

  // Exact matches first
  const exactMatches = suggestions.filter(station =>
    station.toLowerCase() === normalizedQuery
  );
  results.push(...exactMatches);

  // Starts with matches
  if (results.length < limit) {
    const startsWithMatches = suggestions.filter(station =>
      station.toLowerCase().startsWith(normalizedQuery) &&
      !results.includes(station)
    );
    results.push(...startsWithMatches.slice(0, limit - results.length));
  }

  // Contains matches
  if (results.length < limit) {
    const containsMatches = suggestions.filter(station =>
      station.toLowerCase().includes(normalizedQuery) &&
      !results.includes(station)
    );
    results.push(...containsMatches.slice(0, limit - results.length));
  }

  // Fuzzy matches using Levenshtein distance
  if (results.length < limit) {
    const fuzzyMatches = suggestions
      .filter(station => !results.includes(station))
      .map(station => ({
        station,
        distance: levenshteinDistance(normalizedQuery, station.toLowerCase())
      }))
      .filter(item => item.distance <= 3)
      .sort((a, b) => a.distance - b.distance)
      .map(item => item.station);

    results.push(...fuzzyMatches.slice(0, limit - results.length));
  }

  return results.slice(0, limit);
}

/**
 * Validate if a station name exists in the suggestions
 * @param {string} stationName - Station name to validate
 * @param {Array<string>} suggestions - Array of valid station suggestions
 * @returns {boolean} - True if station exists, false otherwise
 */
export function validateStation(stationName, suggestions) {
  if (!stationName || !suggestions || suggestions.length === 0) {
    return false;
  }

  const normalizedStation = stationName.toLowerCase().trim();
  return suggestions.some(station =>
    station.toLowerCase() === normalizedStation
  );
}

/**
 * Get the exact station name from suggestions (case-sensitive)
 * @param {string} stationName - Station name to find
 * @param {Array<string>} suggestions - Array of station suggestions
 * @returns {string|null} - Exact station name or null if not found
 */
export function getExactStationName(stationName, suggestions) {
  if (!stationName || !suggestions || suggestions.length === 0) {
    return null;
  }

  const normalizedStation = stationName.toLowerCase().trim();
  return suggestions.find(station =>
    station.toLowerCase() === normalizedStation
  ) || null;
}
